// A habit, as stored in `habits/{id}` (Habits.swift: Habit, HabitSchedule, HabitPause; docs/63). A collection of its
// own: older versions don't see it. Each day the habit is due it puts one ordinary to-do into Today (`Todo.habitID`).
//
// Like the template's, its decoder (written by hand in the kit) gives every missing field but the id a default, so a
// habit never fails to load: a schedule or breaks that can't be read become every day and none. The schedule's kind
// is a string: one a later version adds is kept as it came, and nothing is due from it here.

import { newID } from "../ids";
import { storedDay } from "../days";
import type { Day } from "../types";
import { readRecord } from "./shared";
import { DISTANT_PAST } from "./template";
import {
  decoding, document, readArray, readInt, readStruct, storedFields, withoutUndefined,
  type DecodeContext, type Decoded, type EncodeOptions, type Fields, type ValueReader,
} from "./values";

/** The kinds this version knows (HabitSchedule.Kind); any other string is a later version's. */
export const HABIT_KINDS = ["everyNDays", "weekdays", "timesPerWeek", "timesPerMonth", "monthDays"] as const;
export type HabitKind = (typeof HABIT_KINDS)[number];

/** How often a habit is due: on set days, or N times a week or month on any days. */
export interface HabitSchedule {
  /** One of `HABIT_KINDS`, or a later version's kind, kept as it came. */
  kind: string;
  /** Every N days (everyNDays; 1 is every day). */
  interval?: number;
  /** 1 = Sunday … 7 = Saturday (weekdays). */
  weekdays?: number[];
  /** Times per week or month. */
  count?: number;
  /** 1 … 31 (monthDays). */
  monthDays?: number[];
}

/** How Often as it was before a change, and the last day it held (Habits.swift: HabitEarlierSchedule). */
export interface HabitEarlierSchedule {
  schedule: HabitSchedule;
  until?: Day;
}

/** A break: nothing is due from `from` until `until`, both included. */
export interface HabitPause {
  from?: Day;
  until?: Day;
}

export interface Habit {
  id: string;
  title: string;
  notes: string;
  areaID?: string;
  schedule: HabitSchedule;
  /** How Often before each change, oldest first, each with the last day it held: the past keeps the rule it had.
   *  `schedule` holds from the day after the last of them. Absent: never changed. */
  earlierSchedules?: HabitEarlierSchedule[];
  /** Never Miss Twice; absent means on. */
  neverMissTwice?: boolean;
  /** The first day it counts. */
  startsOn?: Day;
  /** The time of its reminder, as a moment on any day (only the hour and minute count, read in `reminderZone`). */
  reminder?: Date;
  reminderZone?: string;
  pauses?: HabitPause[];
  /** A flexible habit taken off this day with Not Today. */
  restsOn?: Day;
  /** Turned back into a to-do or archived: it stops, its history stays. */
  archivedAt?: Date;
  /** The to-do it became with Make a To-Do (docs/63 15e), so the flame on that to-do, or on a later copy of it, brings
   *  this habit back with its history (formerHabit). Absent: it never was one, or it runs again. */
  becameTodoID?: string;
  /** The milestones already celebrated, so each card comes once on all devices. */
  milestonesShown?: number[];
  order: number;
  createdAt: Date;
  isSample?: boolean;
  unknownFields: Fields;
}

export const HABIT_FIELDS = [
  "id", "title", "notes", "areaID", "schedule", "earlierSchedules", "neverMissTwice", "startsOn", "reminder", "reminderZone", "pauses", "restsOn",
  "archivedAt", "becameTodoID", "milestonesShown", "order", "createdAt", "isSample",
] as const;

/** Every day. */
export const DAILY: HabitSchedule = { kind: "everyNDays", interval: 1 };

export function makeHabit(values: Partial<Habit> = {}): Habit {
  return { id: newID(), title: "", notes: "", schedule: { ...DAILY }, order: 0, createdAt: new Date(), unknownFields: {}, ...values };
}

const readInts: ValueReader<number[]> = readArray(readInt);

export const readHabitSchedule: ValueReader<HabitSchedule> = readStruct((fields) => withoutUndefined<HabitSchedule>({
  kind: fields.optionalString("kind") ?? "everyNDays",
  interval: fields.optionalInt("interval"),
  weekdays: fields.optional("weekdays", readInts),
  count: fields.optionalInt("count"),
  monthDays: fields.optional("monthDays", readInts),
}));

const readPause: ValueReader<HabitPause> = readStruct((fields) => withoutUndefined<HabitPause>({
  from: fields.optionalDay("from"),
  until: fields.optionalDay("until"),
}));

// As the kit's decoder: a schedule that can't be read is every day.
const readEarlierSchedule: ValueReader<HabitEarlierSchedule> = readStruct((fields) => {
  let schedule: HabitSchedule | undefined;
  try { schedule = fields.optional("schedule", readHabitSchedule); } catch { schedule = undefined; }
  return withoutUndefined<HabitEarlierSchedule>({ schedule: schedule ?? { ...DAILY }, until: fields.optionalDay("until") });
});

export function decodeHabit(data: Fields, context: DecodeContext): Decoded<Habit> {
  return decoding(() => readRecord(data, context, (fields) => {
    // As the kit's `try?`: what can't be read takes its default rather than losing the habit.
    const attempt = <T>(read: () => T | undefined): T | undefined => {
      try { return read(); } catch { return undefined; }
    };
    return withoutUndefined<Habit>({
      id: fields.uuid("id"),
      title: fields.optionalString("title") ?? "",
      notes: fields.optionalString("notes") ?? "",
      areaID: fields.optionalUUID("areaID"),
      schedule: attempt(() => fields.optional("schedule", readHabitSchedule)) ?? { ...DAILY },
      earlierSchedules: attempt(() => fields.optional("earlierSchedules", readArray(readEarlierSchedule))),
      neverMissTwice: fields.optionalBool("neverMissTwice"),
      startsOn: fields.optionalDay("startsOn"),
      reminder: fields.optionalMoment("reminder"),
      reminderZone: fields.optionalString("reminderZone"),
      pauses: attempt(() => fields.optional("pauses", readArray(readPause))),
      restsOn: fields.optionalDay("restsOn"),
      archivedAt: fields.optionalMoment("archivedAt"),
      becameTodoID: attempt(() => fields.optionalUUID("becameTodoID")),
      milestonesShown: fields.optional("milestonesShown", readInts),
      order: fields.optionalNumber("order") ?? 0,
      createdAt: fields.optionalMoment("createdAt") ?? DISTANT_PAST,
      isSample: fields.optionalBool("isSample"),
      unknownFields: fields.unknownFields(HABIT_FIELDS),
    });
  }));
}

/** The schedule as the kit writes it: the kind always, the rest when set, the weekdays and days sorted. */
export function encodeHabitSchedule(schedule: HabitSchedule): Fields {
  return document({}, {
    kind: schedule.kind,
    interval: schedule.interval,
    weekdays: schedule.weekdays && [...schedule.weekdays].sort((a, b) => a - b),
    count: schedule.count,
    monthDays: schedule.monthDays && [...schedule.monthDays].sort((a, b) => a - b),
  });
}

export function encodeHabit(habit: Habit, options?: EncodeOptions): Fields {
  return storedFields(document(habit.unknownFields, {
    id: habit.id,
    title: habit.title,
    notes: habit.notes,
    areaID: habit.areaID,
    schedule: encodeHabitSchedule(habit.schedule),
    earlierSchedules: habit.earlierSchedules?.map((earlier) => document({}, {
      schedule: encodeHabitSchedule(earlier.schedule),
      until: earlier.until === undefined ? undefined : storedDay(earlier.until),
    })),
    neverMissTwice: habit.neverMissTwice,
    startsOn: habit.startsOn === undefined ? undefined : storedDay(habit.startsOn),
    reminder: habit.reminder,
    reminderZone: habit.reminderZone,
    pauses: habit.pauses?.map((pause) => document({}, {
      from: pause.from === undefined ? undefined : storedDay(pause.from),
      until: pause.until === undefined ? undefined : storedDay(pause.until),
    })),
    restsOn: habit.restsOn === undefined ? undefined : storedDay(habit.restsOn),
    archivedAt: habit.archivedAt,
    becameTodoID: habit.becameTodoID,
    milestonesShown: habit.milestonesShown && [...habit.milestonesShown],
    order: habit.order,
    createdAt: habit.createdAt,
    isSample: habit.isSample,
  }), options);
}
